import { compact, first } from 'lodash';

import { getTypeOrUndefined } from '@/modules/entities/EntitySubforms/utils/shared/common.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import {
  AugmentedCreateDispositionScenarioInput,
  AugmentedCreateDispositiveProvisionInput,
  AugmentedUpdateDispositionScenarioInput,
  AugmentedUpdateEntityInput,
  AugmentedUpdateHouseholdInput,
  AugmentedUpdateTestamentaryEntityInput,
  DispositiveProvisionDispositionKind,
  DispositiveProvisionRecipientKind,
  TransferMethod,
  UpdateTestamentaryEntityInput,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { DispositionScheme } from '../dispositiveProvisions.types';
import {
  DispositiveProvisions_DispositionScenarioFragment,
  DispositiveProvisions_DispositiveProvisionFragment,
} from '../graphql/DispositiveProvisions.fragments.generated';
import {
  DispositiveProvisionsForm_DispositionScenario,
  DispositiveProvisionsFormRecipient,
  RecipientKind,
  RecipientWithId,
} from './DispositiveProvisionsForm.types';
import { SURVIVING_SPOUSE_SENTINEL } from './hooks/useRecipientOptions';

function getRecipientTypeFromFormInput(recipientId: string) {
  const pulidKind = getPulidKind(recipientId);

  const recipientIds: {
    entityID?: string;
    individualID?: string;
    organizationID?: string;
    testamentaryEntityID?: string;
  } = {};

  if (recipientId === SURVIVING_SPOUSE_SENTINEL) {
    return recipientIds;
  }

  if (!pulidKind) {
    throw new Error('Could not get pulid kind');
  }

  if (pulidKind === PulidKind.Entity) {
    recipientIds.entityID = recipientId;
  } else if (pulidKind === PulidKind.ClientProfile) {
    recipientIds.individualID = recipientId;
  } else if (pulidKind === PulidKind.ClientOrganization) {
    recipientIds.organizationID = recipientId;
  } else if (pulidKind === PulidKind.TestamentaryEntity) {
    recipientIds.testamentaryEntityID = recipientId;
  } else {
    throw new UnreachableError({
      case: pulidKind as never,
      message: 'Unrecognized pulid kind',
    });
  }

  return recipientIds;
}

// Convert the form disposition kind, which is split across two fields
// (dispositionKind_ButtonGroup and dispositionKind_Select), into the API disposition kind (which is a single field)
function $getApiDispositionKind(
  r: DispositiveProvisionsFormRecipient
): DispositiveProvisionDispositionKind {
  const kind = r.dispositionKind_ButtonGroup;

  if (!kind || kind === 'other') {
    const otherKind = r.dispositionKind_Select;

    if (!otherKind) {
      throw new Error('Disposition kind is required when other is selected');
    }

    return otherKind;
  }

  // Asset DPs use Percentage on the backend — the percentage value represents
  // the share of the selected assets being distributed.
  if (kind === 'asset') {
    return DispositiveProvisionDispositionKind.Percentage;
  }

  return kind;
}

export type GetCreateDispositiveProvisionsInputOpts =
  | {
      entityId: string;
      entityType: EntityType;
      entitySubtypeId: string;
      isOnHypotheticalWaterfall: boolean;
      waterfallId?: string;
    }
  | {
      testamentaryEntityId: string;
      isOnHypotheticalWaterfall: boolean;
      waterfallId?: string;
    }
  | {
      clientProfileId: string;
      householdId: string;
      isOnHypotheticalWaterfall: boolean;
      waterfallId?: string;
    };

export enum NodesWithDispositiveProvisions {
  Entity = 'entity',
  TestamentaryEntity = 'testamentaryEntity',
  ClientProfile = 'clientProfile',
}

type DispositiveProvisionsInput =
  | {
      type: NodesWithDispositiveProvisions.TestamentaryEntity;
      input: AugmentedUpdateTestamentaryEntityInput;
    }
  | {
      type: NodesWithDispositiveProvisions.Entity;
      input: AugmentedUpdateEntityInput;
    }
  | {
      type: NodesWithDispositiveProvisions.ClientProfile;
      input: [AugmentedUpdateHouseholdInput];
    };

export function isRecipientWithId(
  recipient: DispositiveProvisionsFormRecipient
): recipient is RecipientWithId {
  return !!recipient.recipientId;
}

export const makeCreateProvisionInputsFromRecipient = (
  r: RecipientWithId,
  opts: GetCreateDispositiveProvisionsInputOpts,
  householdId: string
): AugmentedCreateDispositiveProvisionInput[] => {
  const { entityID, individualID, organizationID, testamentaryEntityID } =
    getRecipientTypeFromFormInput(r.recipientId);

  const { isOnHypotheticalWaterfall, waterfallId } = opts;

  if (!r.transferTaxKind) {
    throw new Error('Transfer tax kind is required');
  }
  const dispositionKind = $getApiDispositionKind(r);
  const isAssetDp = r.dispositionKind_ButtonGroup === 'asset';

  const isPercentage =
    dispositionKind === DispositiveProvisionDispositionKind.Percentage;
  const isAmount =
    dispositionKind === DispositiveProvisionDispositionKind.Amount;
  // All non-percentage, non-amount kinds are formulaic (e.g. AnythingLeftOver,
  // RemainingLifetimeExclusionOfGrantor, etc.)
  const isFormulaic = !isPercentage && !isAmount;

  // Strip stale form state that is incompatible with the selected disposition
  // kind so it doesn't leak into the mutation.
  const dispositionAmount = isAmount ? r.dispositionAmount : null;
  // For asset DPs, the percentage comes from dispositionAssetPercentage
  // (the share of the selected assets), not dispositionPercentage.
  const dispositionPercentage = isAssetDp
    ? r.dispositionAssetPercentage
    : isPercentage
      ? r.dispositionPercentage
      : null;
  const dispositionFormulaicPercentage = isFormulaic
    ? r.dispositionFormulaicPercentage
    : null;
  // dispositionLimit only applies to percentage distributions (not asset DPs)
  const dispositionLimit =
    isPercentage && !isAssetDp ? r.dispositionLimit : null;

  // Shared fields for all DPs created from this recipient.
  // dispositionOrder is set to 0 as a placeholder; the caller reassigns
  // sequential orders after flattening all recipients.
  const sharedCreateFields = {
    householdID: householdId,
    dispositionAmount,
    dispositionPercentage,
    dispositionFormulaicPercentage,
    dispositionKind,
    dispositionOrder: 0,
    notes: r.notes,
    transferTaxKind: r.transferTaxKind,
    bearsTax: r.bearsTax ?? false,
    dispositionLimit,
    growByInflation: r.growByInflation,
    transferMethod: r.transferMethod,
    recipientKind:
      r.recipientId === SURVIVING_SPOUSE_SENTINEL
        ? DispositiveProvisionRecipientKind.SurvivingSpouse
        : undefined,
    entityID,
    individualID,
    organizationID,
    testamentaryEntityID,
    associatedHypotheticalWaterfallID: isOnHypotheticalWaterfall
      ? waterfallId
      : undefined,
  };

  if (!isAssetDp) {
    return [{ create: sharedCreateFields }];
  }

  // The backend does not allow a single DP to have both asset sources
  // (AssetStatics/AssetGroups/OwnershipStakes) and insurance policies.
  // When the user selects both in the same form row, split into two DPs.
  const hasAssetSources =
    r.assetGroupIds.length > 0 ||
    r.assetStaticIds.length > 0 ||
    r.ownershipStakeIds.length > 0;
  const hasInsurancePolicies = r.insurancePolicyIds.length > 0;

  if (hasAssetSources && hasInsurancePolicies) {
    return [
      {
        create: {
          ...sharedCreateFields,
          assetGroupIDs: r.assetGroupIds,
          assetStaticIDs: r.assetStaticIds,
          ownershipStakeIDs: r.ownershipStakeIds,
        },
      },
      {
        create: {
          ...sharedCreateFields,
          // Insurance policies always transfer in kind (death benefit payout)
          transferMethod: TransferMethod.InKind,
          insurancePolicyIDs: r.insurancePolicyIds,
        },
      },
    ];
  }

  // Only one type of source — safe to include all on a single DP
  return [
    {
      create: {
        ...sharedCreateFields,
        assetGroupIDs: r.assetGroupIds,
        assetStaticIDs: r.assetStaticIds,
        insurancePolicyIDs: r.insurancePolicyIds,
        ownershipStakeIDs: r.ownershipStakeIds,
      },
    },
  ];
};

/**
 * Flattens a list of (possibly empty) recipients into provision-create inputs
 * and assigns a sequential `dispositionOrder` so split DPs end up in the
 * correct order.
 *
 * Shared by the DP form and the Beneficiary Designations form so any future
 * changes to per-recipient mapping or ordering apply to both.
 */
export function buildSequentialProvisionInputs(
  recipients: DispositiveProvisionsFormRecipient[] | null | undefined,
  opts: GetCreateDispositiveProvisionsInputOpts,
  householdId: string
): AugmentedCreateDispositiveProvisionInput[] {
  return compact(
    recipients?.flatMap((r) => {
      // skip recipients with no selected target
      if (!isRecipientWithId(r)) return null;
      return makeCreateProvisionInputsFromRecipient(r, opts, householdId);
    }) ?? []
  ).map((input, idx) => ({
    ...input,
    create: { ...input.create, dispositionOrder: idx },
  }));
}

// ----------------------------------------------------------------------------------
// Helpers for working with the new "map" structure coming from getFormValues().
// Each disposition scenario is stored under its DispositionScheme key.
// We keep the per-scenario processing logic exactly the same as before and just
// iterate over the map where necessary in the higher-level helpers.
// ----------------------------------------------------------------------------------

function makeDispositiveProvisionsInputs(
  scenarioFormValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  opts: GetCreateDispositiveProvisionsInputOpts,
  householdId: string
): {
  withDispositiveProvisions: AugmentedCreateDispositiveProvisionInput[];
  withSecondDeathDispositiveProvisions: AugmentedCreateDispositiveProvisionInput[];
} {
  const withDispositiveProvisions: AugmentedCreateDispositiveProvisionInput[] =
    [];
  const withSecondDeathDispositiveProvisions: AugmentedCreateDispositiveProvisionInput[] =
    [];

  (
    [
      DispositionScheme.UPON_FIRST_DEATH,
      DispositionScheme.UPON_SECOND_DEATH,
    ] as const
  ).forEach((scheme) => {
    const provisions = scenarioFormValues[scheme];
    if (!provisions) {
      return;
    }

    const { templateId, recipients } = provisions;

    const inputs = buildSequentialProvisionInputs(
      recipients,
      opts,
      householdId
    );

    // If there is a templateId, for the given scheme, we don't need to create
    // any new provisions. They will come from the template.
    // Otherwise, set the inputs to the appropriate array based on the scheme.

    if (!templateId && scheme === DispositionScheme.UPON_FIRST_DEATH) {
      withDispositiveProvisions.push(...inputs);
      return;
    }
    if (!templateId && scheme === DispositionScheme.UPON_SECOND_DEATH) {
      withSecondDeathDispositiveProvisions.push(...inputs);
      return;
    }
  });

  return {
    withDispositiveProvisions,
    withSecondDeathDispositiveProvisions,
  };
}

// there's no "delete" with disposition scenarios because there's always one per primary client,
// and we automatically delete them on the backend when we delete the client (if that happens)
type WithDispositionScenariosReturn =
  | { type: 'update'; input: AugmentedUpdateDispositionScenarioInput }
  | { type: 'create'; input: AugmentedCreateDispositionScenarioInput };

function makeDispositionScenariosInputs(
  formValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  initialFormValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  opts: GetCreateDispositiveProvisionsInputOpts,
  householdId: string
): WithDispositionScenariosReturn {
  const { withDispositiveProvisions, withSecondDeathDispositiveProvisions } =
    makeDispositiveProvisionsInputs(formValues, opts, householdId);

  // The first grantor death ID should be the same, even if there are multiple
  // entries in the formValues map.
  const firstGrantorDeathID = first(
    compact(
      Object.values(formValues).map(
        (fv) => fv?.selectedFirstPrimaryClientDeathId
      )
    )
  );
  if (!firstGrantorDeathID) {
    throw new Error('First grantor death ID is required');
  }

  const sharedProperties = {
    firstGrantorDeathID,
    associatedHypotheticalWaterfallID: opts.isOnHypotheticalWaterfall
      ? opts.waterfallId
      : undefined,
    reviewedAt: new Date(),
    dispositiveProvisionsTemplateID:
      formValues[DispositionScheme.UPON_FIRST_DEATH]?.templateId,
    secondDeathDispositiveProvisionsTemplateID:
      formValues[DispositionScheme.UPON_SECOND_DEATH]?.templateId,
    firstDeathResiduaryPaysAllTaxes:
      formValues[DispositionScheme.UPON_FIRST_DEATH]?._residuaryPaysAllTaxes,
    secondDeathResiduaryPaysAllTaxes:
      formValues[DispositionScheme.UPON_SECOND_DEATH]?._residuaryPaysAllTaxes,
    transferMethod:
      formValues[DispositionScheme.UPON_FIRST_DEATH]?.transferMethod,
  };

  // The associatedWaterfallId should be the same, even if there are multiple
  // entries in the formValues map.
  const initialAssociatedWaterfallId = first(
    compact(
      Object.values(initialFormValues).map(
        (fv) => fv?._dispositionScenarioAssociatedWaterfallId
      )
    )
  );
  const shouldCreateNewHypotheticalScenario = (() => {
    if (!opts.isOnHypotheticalWaterfall) return false;
    return opts.waterfallId !== initialAssociatedWaterfallId;
  })();

  // The dispositionScenarioId should be the same, even if there are multiple
  // entries in the formValues map.
  const dispositionScenarioId = first(
    compact(Object.values(formValues).map((fv) => fv?.dispositionScenarioId))
  );

  if (dispositionScenarioId && !shouldCreateNewHypotheticalScenario) {
    return {
      type: 'update',
      input: {
        id: dispositionScenarioId,
        update: {
          ...sharedProperties,
          clearDispositiveProvisions: true,
          clearSecondDeathDispositiveProvisions: true,
          clearAssociatedHypotheticalWaterfall:
            !sharedProperties.associatedHypotheticalWaterfallID,
          clearDispositiveProvisionsTemplate:
            sharedProperties.dispositiveProvisionsTemplateID ? undefined : true,
          clearSecondDeathDispositiveProvisionsTemplate:
            sharedProperties.secondDeathDispositiveProvisionsTemplateID
              ? undefined
              : true,
        },
        withDispositiveProvisions,
        withSecondDeathDispositiveProvisions,
      },
    };
  }

  return {
    type: 'create',
    input: {
      create: {
        householdID: householdId,
        ...sharedProperties,
        firstGrantorDeathID,
      },
      withDispositiveProvisions,
      withSecondDeathDispositiveProvisions,
    },
  };
}

function getTestamentaryEntityDispositiveProvisionsInput(
  formValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  initialFormValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  opts: GetCreateDispositiveProvisionsInputOpts,
  householdId: string
): DispositiveProvisionsInput {
  if (!('testamentaryEntityId' in opts)) {
    throw new Error('Testamentary entity id is required');
  }

  const { testamentaryEntityId, isOnHypotheticalWaterfall } = opts;

  const update: UpdateTestamentaryEntityInput = {
    // anything handled at the top level by provisions has been moved to the scenario
    clearDispositiveProvisions: true,
    clearHypotheticalDispositiveProvisions: true,
    clearHypotheticalDispositiveProvisionsTemplates: true,
  };

  const { type, input } = makeDispositionScenariosInputs(
    formValues,
    initialFormValues,
    opts,
    householdId
  );

  const withDispositionScenariosInput = isOnHypotheticalWaterfall
    ? {
        withHypotheticalDispositionScenarios:
          type === 'create' ? [input] : undefined,
        updateHypotheticalDispositionScenarios:
          type === 'update' ? [input] : undefined,
      }
    : {
        withDispositionScenarios: type === 'create' ? [input] : undefined,
        updateDispositionScenarios: type === 'update' ? [input] : undefined,
      };

  // We can just use the first templateId and dispositionScheme, because
  // testamentary entities will only have either FIRST_DEATH or SECOND_DEATH
  // form values, while the other one will be null.
  const templateId = first(
    compact(Object.values(formValues).map((fv) => fv?.templateId))
  );
  const dispositionScheme = first(
    compact(Object.values(formValues).map((fv) => fv?.dispositionScheme))
  );

  if (
    !withDispositionScenariosInput.withDispositionScenarios?.length &&
    !withDispositionScenariosInput.updateDispositionScenarios?.length &&
    !withDispositionScenariosInput.withHypotheticalDispositionScenarios
      ?.length &&
    !withDispositionScenariosInput.updateHypotheticalDispositionScenarios
      ?.length
  ) {
    diagnostics.error(
      `No disposition scenarios present on input for ${opts.testamentaryEntityId}`,
      undefined,
      {
        ...opts,
        templateId,
        dispositionScheme,
      }
    );
  }

  return {
    type: NodesWithDispositiveProvisions.TestamentaryEntity,
    input: {
      id: testamentaryEntityId,
      update: {
        ...update,
        reviewedAt: new Date(),
        dispositiveProvisionsTemplateID: getTypeOrUndefined(templateId),
        clearDispositiveProvisionsTemplate: templateId ? undefined : true,
      },
      ...withDispositionScenariosInput,
    },
  };
}

function getClientProfileDispositiveProvisionsInput(
  formValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  initialFormValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  opts: GetCreateDispositiveProvisionsInputOpts,
  householdId: string
): DispositiveProvisionsInput {
  if (!('clientProfileId' in opts)) {
    throw new Error(
      'Invalid data passed to getClientProfileDispositiveProvisionsInput'
    );
  }
  const {
    householdId: optsHouseholdId,
    clientProfileId,
    isOnHypotheticalWaterfall,
  } = opts;

  const { type, input } = makeDispositionScenariosInputs(
    formValues,
    initialFormValues,
    opts,
    householdId
  );

  const withDispositionScenariosInput = isOnHypotheticalWaterfall
    ? {
        withHypotheticalDispositionScenarios:
          type === 'create' ? [input] : undefined,
        updateHypotheticalDispositionScenarios:
          type === 'update' ? [input] : undefined,
      }
    : {
        withDispositionScenarios: type === 'create' ? [input] : undefined,
        updateDispositionScenarios: type === 'update' ? [input] : undefined,
      };

  return {
    type: NodesWithDispositiveProvisions.ClientProfile,
    input: [
      {
        id: optsHouseholdId,
        update: {},
        updateClientProfiles: [
          {
            id: clientProfileId,
            update: {},
            ...withDispositionScenariosInput,
          },
        ],
      },
    ],
  };
}

function getEntityDispositiveProvisionsInput(
  formValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  initialFormValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  opts: GetCreateDispositiveProvisionsInputOpts,
  householdId: string
): DispositiveProvisionsInput {
  if (!('entityId' in opts)) {
    throw new Error(
      'Invalid data passed to getEntityDispositiveProvisionsInput'
    );
  }
  const { entityId, entityType, entitySubtypeId } = opts;

  // Check if we're unlinking a template (templateId was changed from a value to null)
  const isUnlinkingTemplateFirstDeath =
    initialFormValues[DispositionScheme.UPON_FIRST_DEATH]?.templateId &&
    !formValues[DispositionScheme.UPON_FIRST_DEATH]?.templateId;
  const isUnlinkingTemplateSecondDeath =
    initialFormValues[DispositionScheme.UPON_SECOND_DEATH]?.templateId &&
    !formValues[DispositionScheme.UPON_SECOND_DEATH]?.templateId;

  const sharedProperties = {
    id: entityId,
    update: {},
  };

  const { type, input } = makeDispositionScenariosInputs(
    formValues,
    initialFormValues,
    opts,
    householdId
  );

  // For disposition scenarios, we need to make sure the template gets unlinked
  // Ensure clearDispositiveProvisionsTemplate is set in the appropriate path
  if (type === 'update') {
    if (isUnlinkingTemplateFirstDeath) {
      input.update.clearDispositiveProvisionsTemplate = true;
    }
    if (isUnlinkingTemplateSecondDeath) {
      input.update.clearSecondDeathDispositiveProvisionsTemplate = true;
    }
  }

  const withDispositionScenariosInput = opts.isOnHypotheticalWaterfall
    ? {
        withHypotheticalDispositionScenarios:
          type === 'create' ? [input] : undefined,
        updateHypotheticalDispositionScenarios:
          type === 'update' ? [input] : undefined,
      }
    : {
        withDispositionScenarios: type === 'create' ? [input] : undefined,
        updateDispositionScenarios: type === 'update' ? [input] : undefined,
      };

  const updateEntitySubtypeSharedProperties = {
    id: entitySubtypeId,
    update: {},
    ...withDispositionScenariosInput,
  };

  if (
    (updateEntitySubtypeSharedProperties?.withDispositionScenarios?.length ??
      0) > 2
  ) {
    throw new Error('Too many disposition scenarios. There can only be two.');
  }

  if (
    (updateEntitySubtypeSharedProperties?.withHypotheticalDispositionScenarios
      ?.length ?? 0) > 2
  ) {
    throw new Error(
      'Too many hypothetical disposition scenarios. There can only be two.'
    );
  }

  // TODO here: consider writing directly to the disposition scenarios here, if we're only going to be updating one
  // at a time versus updating both at once as happens currently
  const entityInput: AugmentedUpdateEntityInput = (() => {
    switch (entityType) {
      case 'revocable-trust':
        return {
          ...sharedProperties,
          updateRevocableTrust: updateEntitySubtypeSharedProperties,
        };
      case 'irrevocable-trust':
        return {
          ...sharedProperties,
          updateIrrevocableTrust: updateEntitySubtypeSharedProperties,
        };
      case 'slat':
        return {
          ...sharedProperties,
          updateSlatTrust: updateEntitySubtypeSharedProperties,
        };
      case 'grat':
        return {
          ...sharedProperties,
          updateGratTrust: updateEntitySubtypeSharedProperties,
        };
      case 'qprt':
        return {
          ...sharedProperties,
          updateQprtTrust: updateEntitySubtypeSharedProperties,
        };
      case 'ilit':
        return {
          ...sharedProperties,
          updateIlitTrust: updateEntitySubtypeSharedProperties,
        };
      case 'individual-account':
        return {
          ...sharedProperties,
          updateIndividualPersonalAccount: updateEntitySubtypeSharedProperties,
        };
      case 'joint-account':
        return {
          ...sharedProperties,
          updateJointPersonalAccount: updateEntitySubtypeSharedProperties,
        };
      case 'custodial-account':
        return {
          ...sharedProperties,
          updateCustodialPersonalAccount: updateEntitySubtypeSharedProperties,
        };
      case 'retirement-account':
        return {
          ...sharedProperties,
          updateRetirementPersonalAccount: updateEntitySubtypeSharedProperties,
        };
      case 'qualified-tuition-account':
        return {
          ...sharedProperties,
          updateQualifiedTuitionPersonalAccount:
            updateEntitySubtypeSharedProperties,
        };
      case 'clt':
        return {
          ...sharedProperties,
          updateCltTrust: updateEntitySubtypeSharedProperties,
        };
      case 'crt':
        return {
          ...sharedProperties,
          updateCrtTrust: updateEntitySubtypeSharedProperties,
        };
      case 'daf':
        return {
          ...sharedProperties,
          updateDonorAdvisedFund: updateEntitySubtypeSharedProperties,
        };
      case 'private-foundation':
        return {
          ...sharedProperties,
          updatePrivateFoundation: updateEntitySubtypeSharedProperties,
        };

      case 'insurance-account':
        return {
          ...sharedProperties,
          updateInsurancePersonalAccount: updateEntitySubtypeSharedProperties,
        };

      case 'sole-proprietorship':
      case 'c-corp':
      case 's-corp':
      case 'llc':
      case 'lp':
      case 'gp':
        // https://linear.app/luminary/issue/LUM-1878/add-dispositive-provisions-to-business-entities
        throw new Error('Not implemented');

      default:
        throw new UnreachableError({
          case: entityType,
          message: `Unrecognized entity type ${entityType}`,
        });
    }
  })();

  return {
    type: NodesWithDispositiveProvisions.Entity,
    input: entityInput,
  };
}

export function getCreateDispositiveProvisionsInput(
  form: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  initialForm: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  opts: GetCreateDispositiveProvisionsInputOpts,
  householdId: string
): DispositiveProvisionsInput {
  if ('testamentaryEntityId' in opts) {
    return getTestamentaryEntityDispositiveProvisionsInput(
      form,
      initialForm,
      opts,
      householdId
    );
  }

  if ('clientProfileId' in opts) {
    return getClientProfileDispositiveProvisionsInput(
      form,
      initialForm,
      opts,
      householdId
    );
  }

  return getEntityDispositiveProvisionsInput(
    form,
    initialForm,
    opts,
    householdId
  );
}

/**
 * @description Returns the recipient id and kind for a given provision.
 */
export function getRecipientDetails(
  provision: DispositiveProvisions_DispositiveProvisionFragment
): {
  recipientId: string;
  recipientKind: RecipientKind;
} {
  if (
    provision.recipientKind ===
    DispositiveProvisionRecipientKind.SurvivingSpouse
  ) {
    return {
      recipientId: SURVIVING_SPOUSE_SENTINEL,
      recipientKind: RecipientKind.SurvivingSpouse,
    };
  }
  if (provision.entity) {
    return {
      recipientId: provision.entity.id,
      recipientKind: RecipientKind.Entity,
    };
  }

  if (provision.individual) {
    return {
      recipientId: provision.individual.id,
      recipientKind: RecipientKind.Individual,
    };
  }

  if (provision.organization) {
    return {
      recipientId: provision.organization.id,
      recipientKind: RecipientKind.Organization,
    };
  }

  if (provision.testamentaryEntity) {
    return {
      recipientId: provision.testamentaryEntity.id,
      recipientKind: RecipientKind.TestamentaryEntity,
    };
  }

  throw new UnreachableError({
    case: provision as never,
    message: 'Could not find recipient kind',
  });
}

/**
 * @description Returns the disposition kind and disposition kind other for a given provision.
 */
export function getDispositionKind(
  // Only reads `dispositionKind`; accepts any provision-shaped object (e.g. the
  // diagram's terminal provision fragment) so callers outside the form can
  // reuse the same disposition-kind derivation.
  provision: Pick<
    DispositiveProvisions_DispositiveProvisionFragment,
    'dispositionKind'
  >
): {
  dispositionKind_ButtonGroup: DispositiveProvisionsFormRecipient['dispositionKind_ButtonGroup'];
  dispositionKind_Select: DispositiveProvisionsFormRecipient['dispositionKind_Select'];
} {
  if (provision.dispositionKind) {
    if (
      provision.dispositionKind ===
        DispositiveProvisionDispositionKind.AnythingLeftOver ||
      provision.dispositionKind ===
        DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion ||
      provision.dispositionKind ===
        DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantor ||
      provision.dispositionKind ===
        DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor ||
      provision.dispositionKind ===
        DispositiveProvisionDispositionKind.RemainingStateExemption ||
      provision.dispositionKind ===
        DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption ||
      provision.dispositionKind ===
        DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption
    ) {
      return {
        dispositionKind_ButtonGroup: 'other',
        dispositionKind_Select: provision.dispositionKind,
      };
    }

    if (
      provision.dispositionKind !==
        DispositiveProvisionDispositionKind.Amount &&
      provision.dispositionKind !==
        DispositiveProvisionDispositionKind.Percentage
    ) {
      throw new Error(
        'Invalid disposition kind, expected amount or percentage'
      );
    }

    return {
      dispositionKind_ButtonGroup: provision.dispositionKind,
      dispositionKind_Select: '',
    };
  }

  return {
    dispositionKind_ButtonGroup: DispositiveProvisionDispositionKind.Percentage,
    dispositionKind_Select: '',
  };
}

export interface GetInitialValuesParams {
  primaryClientIds: string[];
  dispositionScenarios?: DispositiveProvisions_DispositionScenarioFragment[];
  isClientProfile: boolean;
  clientProfileOrEntityOrTestamentaryEntityId: string;
  firstDeathPrimaryClientId: string;
  isTestamentaryEntity: boolean;
}
